import React from 'react';
import { fieldClass, helperClass, HelpHint, labelClass, SearchableSelect } from '../../governed-mcp-gateway/components/GatewayUi';
import { GOVERNANCE_HELP, LLM_TYPE_OPTIONS, providerLabel, recommendedApiTypes } from '../llmGatewayConstants';
import type { GatewayFormState, LlmType, RegisteredLlm } from '../llmGatewayTypes';
import ModelInfoCard from '../components/ModelInfoCard';

export default function SelectLlmStep({
  form,
  errors,
  isLight,
  models: catalog,
  onChange,
}: {
  form: GatewayFormState;
  errors: Record<string, string>;
  isLight: boolean;
  models: RegisteredLlm[];
  onChange: (next: GatewayFormState) => void;
}) {
  const models = catalog.filter((model) => !form.llmType || model.type === form.llmType);
  const selected = catalog.find((model) => model.id === form.modelId);

  return (
    <div className="space-y-5">
      <div>
        <label className={labelClass(isLight)} htmlFor="gateway-name">
          Gateway Name
        </label>
        <input
          id="gateway-name"
          className={fieldClass(isLight)}
          placeholder="Enter gateway name"
          value={form.name}
          onChange={(event) => onChange({ ...form, name: event.target.value })}
        />
        {errors.name ? <p className="mt-1 text-xs text-red-500">{errors.name}</p> : null}
      </div>

      <div>
        <label className={labelClass(isLight)} htmlFor="llm-type">
          LLM Type
        </label>
        <select
          id="llm-type"
          className={`${fieldClass(isLight)} appearance-none`}
          value={form.llmType}
          onChange={(event) => {
            const llmType = event.target.value as LlmType | '';
            const stillValid = catalog.find((model) => model.id === form.modelId && (!llmType || model.type === llmType));
            onChange({
              ...form,
              llmType,
              modelId: stillValid ? form.modelId : '',
              apiTypes: stillValid ? form.apiTypes : [],
            });
          }}
        >
          <option value="">Select LLM type</option>
          {LLM_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p className={helperClass(isLight)}>Chat and multimodal models come from the LLM catalog. Embeddings come from the embedding catalog.</p>
        {errors.llmType ? <p className="mt-1 text-xs text-red-500">{errors.llmType}</p> : null}
      </div>

      <div>
        <SearchableSelect
          label="Select Registered LLM"
          value={form.modelId}
          options={models.map((model) => ({
            id: model.id,
            label: model.name,
            hint: `${providerLabel(model.provider)} · ${model.catalog === 'embedding' ? 'Embedding' : 'LLM'}`,
          }))}
          onChange={(modelId) => {
            const model = catalog.find((item) => item.id === modelId);
            onChange({
              ...form,
              modelId,
              llmType: model?.type ?? form.llmType,
              apiTypes: model ? recommendedApiTypes(model) : [],
              customEndpoint: model?.servingProtocol === 'custom' ? form.customEndpoint : '',
            });
          }}
          isLight={isLight}
          placeholder={form.llmType ? 'Search registered models' : 'Select an LLM type first'}
          searchPlaceholder="Search models"
        />
        {errors.modelId ? <p className="mt-1 text-xs text-red-500">{errors.modelId}</p> : null}
        {!form.llmType ? (
          <p className={helperClass(isLight)}>
            {catalog.length === 0
              ? 'No models are registered in Portal yet. Add an LLM or embedding provider first.'
              : 'Choose a type to filter previously registered models.'}
          </p>
        ) : models.length === 0 ? (
          <p className={helperClass(isLight)}>No registered models for this type.</p>
        ) : null}
      </div>

      <div>
        <div className={`${labelClass(isLight)} flex items-center gap-1.5`}>
          LLM Provider
          <HelpHint text="Detected from the model’s registered provider mapping." />
        </div>
        <input
          className={`${fieldClass(isLight)} ${isLight ? 'bg-neutral-50 text-neutral-600' : 'bg-neutral-950 text-neutral-400'}`}
          readOnly
          value={selected ? providerLabel(selected.provider) : ''}
          placeholder="Auto-detected when a model is selected"
        />
      </div>

      {selected ? (
        <div>
          <div className={`${labelClass(isLight)} flex items-center gap-1.5`}>
            Model details
            <HelpHint text={GOVERNANCE_HELP.servingProtocol} />
          </div>
          <ModelInfoCard model={selected} isLight={isLight} />
        </div>
      ) : null}
    </div>
  );
}
